import { useApolloClient, useMutation } from '@apollo/client'
import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import List from 'components/v2/List'
import { ActivateAction } from 'components/ActivateAction'
import Dropdown from 'components/v2/Dropdown'
import Snackbar from 'components/Snackbar'
import { DotsIcon } from 'components/Icons/DotsIcon'
import { RegisterSupportInteractionModal } from 'features/Bookings/components/RegisterSupportInteractionModal'
import { DEACTIVATE_COUPON } from 'features/Campaigns/mutations-legacy'
import { GET_TRIP_SUBCAMPAIGN } from 'features/Trips/queries-legacy'
import { GTCouponStatus } from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'
import { resolvers as couponsResolvers, DEACTIVATE_LOCAL_SUBCAMPAIGN_COUPON } from 'resolvers/campaigns'
import { canRegisterSupportInteraction, canResendCoupons, canDeactivateSubcampaignCoupons } from 'features/Users/utils'
import { ITripIncidenceCouponsActionsProps } from './types'
import ResendCouponsModal from '../ResendCouponsModal'
import IconButton from 'components/v2/IconButton'

export const TripIncidenceCouponsActions = ({
  booking,
  campaignId,
  subcampaignId,
  coupon
}: ITripIncidenceCouponsActionsProps): ReactElement => {
  const client = useApolloClient()
  client.addResolvers(couponsResolvers)
  const login = useLogin()
  const [isOpenDropdown, setOpenDropdown] = useState(false)
  const [showSucessSnackbar, setShowSucessSnackbar] = useState(false)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [isOpenRegisterSupportInteractionModal, setOpenRegisterSupportInteractionModal] = useState(false)

  const [deactivateLocalCoupon] = useMutation(DEACTIVATE_LOCAL_SUBCAMPAIGN_COUPON)
  const [deactivateCoupon, { loading: deactivateLoading, error: deactivateError }] = useMutation(DEACTIVATE_COUPON, {
    onCompleted: () => {
      deactivateLocalCoupon({
        variables: {
          id: coupon.id
        }
      })
    }
  })

  const toggleDropdown = (): void => {
    setOpenDropdown(!isOpenDropdown)
  }

  const isResendDisabled = coupon.status === GTCouponStatus.Expired || coupon.status === GTCouponStatus.Redeemed
  const isDeactivateEnabled =
    (coupon.status === GTCouponStatus.Sent || coupon.status === GTCouponStatus.Created) && coupon.isActive
  const handleResendCoupons = (): void => {
    if (isResendDisabled) return
    setIsOpenModal(true)
    setOpenDropdown(false)
  }
  const handleDeactivateCoupon = (): void => {
    if (!isDeactivateEnabled) return

    deactivateCoupon({
      variables: {
        campaignId: campaignId,
        couponId: coupon.id,
        subcampaignId: subcampaignId
      }
    })
  }

  const { policies } = login.accessToken
  const hasRegisterSupportInteractionPermission = canRegisterSupportInteraction(policies)
  const hasResendCouponsPermission = canResendCoupons(policies)
  const hasDeactivateCouponsPermission = canDeactivateSubcampaignCoupons(policies)

  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator onClickAway={(): void => setOpenDropdown(false)}>
        <IconButton icon={<DotsIcon />} onClick={toggleDropdown} noBorder smallPadding />
        <Dropdown.Content open={isOpenDropdown} noIndicator left noOverflow>
          <List.Container>
            {hasResendCouponsPermission && (
              <List.Item action onClick={handleResendCoupons} disabled={isResendDisabled}>
                <FormattedMessage id="createIncidence.action" defaultMessage="Re-send coupons" />
              </List.Item>
            )}
            {hasRegisterSupportInteractionPermission && (
              <List.Item
                action
                onClick={(): void => {
                  setOpenDropdown(false)
                  setOpenRegisterSupportInteractionModal(true)
                }}
              >
                <FormattedMessage
                  id="tripIncidences.coupons.list.actions.registerSupportInteraction"
                  defaultMessage="Notes"
                />
              </List.Item>
            )}
            {hasDeactivateCouponsPermission && (
              <List.Item
                action
                onClick={handleDeactivateCoupon}
                disabled={!isDeactivateEnabled}
                loading={deactivateLoading}
              >
                <ActivateAction isActive={coupon.isActive} error={deactivateError} />
              </List.Item>
            )}
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isOpenRegisterSupportInteractionModal && (
        <RegisterSupportInteractionModal
          booking={booking}
          closeModal={(): void => setOpenRegisterSupportInteractionModal(false)}
          onCompleted={(): void => {
            client.refetchQueries({
              include: [GET_TRIP_SUBCAMPAIGN]
            })
            setOpenRegisterSupportInteractionModal(false)
          }}
        />
      )}
      {isOpenModal && (
        <ResendCouponsModal
          bookingId={booking.id}
          phone={booking.buyer?.phone ?? ''}
          email={booking.buyer?.email ?? ''}
          campaignId={campaignId}
          subcampaignId={subcampaignId}
          couponId={coupon.id}
          closeModal={(): void => {
            setIsOpenModal(false)
          }}
          onCompleted={(): void => {
            setShowSucessSnackbar(true)
          }}
        />
      )}
      {showSucessSnackbar && (
        <Snackbar.Container>
          <Snackbar.Content>
            <FormattedMessage
              id="createIncidence.resendCoupons.success.message"
              defaultMessage="Coupons sent successfully"
            />
          </Snackbar.Content>

          <Snackbar.Action
            onClick={(): void => {
              setShowSucessSnackbar(false)
            }}
          >
            <FormattedMessage id="dictionary.close" defaultMessage="Close" />
          </Snackbar.Action>
        </Snackbar.Container>
      )}
    </>
  )
}
